import { useState, useEffect } from 'react';
import { formatBusinessDate } from '@/lib/format';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import {
    Search, Fuel, Wifi, Users, Wrench, Home, Gift, FileCheck,
    MapPin, Truck, MoreHorizontal, CreditCard,
} from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

type ExpenseType = Database['public']['Enums']['expense_type'];

const EXPENSE_TYPES = [
    { value: 'auto_parts', label: 'Auto Parts', icon: Wrench },
    { value: 'family', label: 'Family', icon: Users },
    { value: 'fuel', label: 'Fuel', icon: Fuel },
    { value: 'internet', label: 'Internet', icon: Wifi },
    { value: 'office', label: 'Office', icon: Home },
    { value: 'referral', label: 'Referral', icon: Gift },
    { value: 'refund', label: 'Refund', icon: CreditCard },
    { value: 'roadworthiness', label: 'Roadworthiness', icon: FileCheck },
    { value: 'salary', label: 'Salary', icon: Users },
    { value: 'tracker', label: 'Tracker', icon: MapPin },
    { value: 'transportation', label: 'Transportation', icon: Truck },
    { value: 'other', label: 'Other', icon: MoreHorizontal },
] as const;

interface ExpensesRecordsProps {
    onEditExpense?: (expenseId: number) => void;
}

export default function ExpensesRecords({ onEditExpense }: ExpensesRecordsProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const [typeFilter, setTypeFilter] = useState<string>('all');


    const { data: countData } = useQuery({
        queryKey: ['expenses-count', debouncedSearch, typeFilter],
        queryFn: async () => {
            let query = supabase.from('expenses').select('*', { count: 'exact', head: true });
            if (debouncedSearch) {
                query = query.ilike('description', `%${debouncedSearch}%`);
            }
            if (typeFilter !== 'all') {
                query = query.eq('type', typeFilter as ExpenseType);
            }
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { resetPage(); }, [debouncedSearch, typeFilter]);

    const { data: expenses, isLoading } = useQuery({
        queryKey: ['expenses', currentPage, debouncedSearch, typeFilter],
        queryFn: async () => {
            let query = supabase
                .from('expenses')
                .select('id, type, description, transactions(amount, transaction_at)')
                .order('created_at', { ascending: false });
            if (debouncedSearch) {
                query = query.ilike('description', `%${debouncedSearch}%`);
            }
            if (typeFilter !== 'all') {
                query = query.eq('type', typeFilter as ExpenseType);
            }
            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
    };



    return (
        <Card className="border-0">
            <CardHeader>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search expenses..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {EXPENSE_TYPES.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <SelectItem key={t.value} value={t.value}>
                                        <div className="flex items-center gap-2">
                                            <Icon className="h-4 w-4" />
                                            {t.label}
                                        </div>
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent>
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={goToPage}
                        totalItems={totalItems}
                    />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : expenses && expenses.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {expenses.map((expense) => (
                                <TableRow
                                    key={expense.id}
                                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => onEditExpense?.(expense.id)}
                                >
                                    <TableCell>
                                        {formatBusinessDate(expense.transactions?.transaction_at)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="capitalize">{expense.type}</Badge>
                                    </TableCell>
                                    <TableCell className="max-w-[300px] truncate">{expense.description || '-'}</TableCell>
                                    <TableCell className="text-right font-medium text-destructive">
                                        {expense.transactions?.amount ? formatCurrency(expense.transactions.amount) : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {searchTerm || typeFilter !== 'all' ? 'No expenses match your filters' : 'No expenses found'}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
